import React from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  TextField,
} from "@mui/material";

// Renders one schema field. `values` is a flat object keyed by field id;
// companion inputs use `${id}_Other`, `${id}Location`, and `${id}_locations`.
// `specialRenderers` maps field.special -> (field) => node for custom fields.
export const SchemaField = ({ field, values, onChange, specialRenderers }) => {
  if (field.special && specialRenderers?.[field.special]) {
    return specialRenderers[field.special](field);
  }

  const value = values?.[field.id];

  if (field.type === "text" || field.type === "textarea") {
    return (
      <TextField
        label={field.label}
        fullWidth
        required={field.required}
        multiline={field.type === "textarea"}
        rows={field.type === "textarea" ? 4 : undefined}
        value={value || ""}
        onChange={(e) => onChange(field.id, e.target.value)}
      />
    );
  }

  if (field.type === "date") {
    return (
      <TextField
        label={field.label}
        fullWidth
        required={field.required}
        type="date"
        value={value || ""}
        onChange={(e) => onChange(field.id, e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  if (field.type === "select") {
    return (
      <FormControl fullWidth required={field.required}>
        <InputLabel id={`${field.id}-label`}>{field.label}</InputLabel>
        <Select
          labelId={`${field.id}-label`}
          label={field.label}
          value={value || ""}
          onChange={(e) => onChange(field.id, e.target.value)}
        >
          {(field.options || []).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (field.type === "multiselect") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-col gap-3">
        <FormControl fullWidth required={field.required}>
          <InputLabel id={`${field.id}-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.id}-label`}
            label={field.label}
            multiple
            value={selected}
            onChange={(e) => onChange(field.id, e.target.value)}
            renderValue={(sel) => sel.join(", ")}
          >
            {(field.options || []).map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={selected.includes(option)} />
                <ListItemText primary={option} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selected.includes("Other") && (
          <TextField
            label={`${field.label} — please specify`}
            fullWidth
            required
            value={values?.[`${field.id}_Other`] || ""}
            onChange={(e) => onChange(`${field.id}_Other`, e.target.value)}
          />
        )}

        {field.locationsWhenMultiple && selected.length > 1 && (
          <TextField
            label={`${field.label} Locations`}
            fullWidth
            required
            value={values?.[`${field.id}Location`] || ""}
            onChange={(e) => onChange(`${field.id}Location`, e.target.value)}
          />
        )}

        {field.locationPerSelection && selected.length > 1 && (
          <div className="flex flex-col gap-3">
            {selected.map((sel) => (
              <TextField
                key={sel}
                label={`Location for ${sel}`}
                fullWidth
                required
                value={values?.[`${field.id}_locations`]?.[sel] || ""}
                onChange={(e) =>
                  onChange(`${field.id}_locations`, {
                    ...(values?.[`${field.id}_locations`] || {}),
                    [sel]: e.target.value,
                  })
                }
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
};

const gridClassFor = (columns) => {
  if (columns === 1) return "grid grid-cols-1 gap-4";
  if (columns === 2) return "grid grid-cols-1 gap-4 md:grid-cols-2";
  return "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3";
};

// Renders a section's field list in a responsive grid.
export const SchemaFields = ({
  fields,
  values,
  onChange,
  columns = 3,
  specialRenderers,
}) => (
  <div className={gridClassFor(columns)}>
    {(fields || []).map((field) => (
      <SchemaField
        key={field.id}
        field={field}
        values={values}
        onChange={onChange}
        specialRenderers={specialRenderers}
      />
    ))}
  </div>
);
